import { useEffect, useState, type CSSProperties } from "react";
import { TrendChart } from "./overview/TrendChart";
import { CostByTokenClass } from "./overview/CostByTokenClass";
import { ConnectedAgentsCard } from "./overview/ConnectedAgentsCard";
import { ModelsCard } from "./overview/ModelsCard";
import { DashboardFilters } from "./overview/DashboardFilters";
import {
  CostReductionCard,
  KpiSkeleton,
  StatCard,
  SubStat,
  TokenBreakdown,
} from "./overview/KpiCards";
import { SAVINGS_HINT, SPEND_HINT } from "./overview/metricHints";
import { Activity, DollarSign, Layers, Plus } from "./overview/icons";
import {
  dashboardNow,
  utcMs,
  formatCompact,
  formatUSD,
  useAgentRollups,
  useFilters,
  useModelRollups,
  savedPct,
  useSummary,
  useTrends,
  windowSavings,
  DAY_MS,
  UNTAGGED_AGENT,
  type Summary,
} from "../analytics";
import {
  DEMO_AGENTS,
  DEMO_MODELS,
  DEMO_SUMMARY,
  demoTrendPoints,
} from "./overview/emptyStateDemo";
import { FailuresPanel } from "./failures/FailuresPanel";
import { trackButtonPressed } from "../analytics/tracking";
import { useAgents } from "../agents";
import { useInsightCounts } from "../insights";
import { useWsNavigate } from "../workspaces";

/* Workspace landing — the "Overview" dashboard (OPSS-104), restyled to the
 * Gateway-App Figma overview (node 284:315): soft-tinted rounded KPI cards
 * (mint feature card with a tick gauge), the connected-agents row list, and
 * the equal-width Saved / Cost-by-Token-Class chart pair. Wired to the
 * analytics hooks (mock client in dev, real gateway when
 * VITE_ANALYTICS_API_BASE is set).
 *
 * The overview never auto-redirects to the wizard: an empty workspace shows the
 * dashboard's own empty states, and the first-run wizard is reached explicitly
 * (the header's "Add Agent", the shareable /welcome link, or post-signup). A
 * derived "no agents ⇒ /welcome" gate here was racy — a stale or empty-body
 * agents response bounced returning users off the overview (see OPSS). */
export default function WorkspaceHome() {
  return <OverviewBody />;
}

/* How often to re-read the analytics while an agent's first rows are landing.
 * The backend rebuilds the rollups every 5 minutes, so this only decides how
 * soon the page notices — not how soon the data exists. */
const PRIMING_POLL_MS = 15_000;

/* How long after the `used` latch flips an empty window still reads as "the
 * rows haven't landed". The latch itself never resets, so it needs the bound. */
const PRIMING_WINDOW_MS = 10 * 60_000;

function OverviewBody() {
  const wsNav = useWsNavigate();
  const {
    range,
    activeFilterCount,
    bumpRefresh,
    models,
    setModels,
    agentIds,
    setAgentIds,
    resetFilters,
    query,
  } = useFilters();
  // The failure drill-down is workspace-wide here (no agent scope), and its
  // reads only fire once it's open.
  const [failuresOpen, setFailuresOpen] = useState(false);

  const summary = useSummary();
  const spanDays = (Date.parse(range.to) - Date.parse(range.from)) / DAY_MS;
  const granularity = spanDays <= 2 ? ("hour" as const) : ("day" as const);
  const trends = useTrends(granularity);
  const rollups = useAgentRollups();
  const modelRollups = useModelRollups();

  // Option lists for the filter menus. Read WITHOUT the model/agent filters, or
  // narrowing to one model would shrink the menu to that model and strand you
  // there. When no filter is active these queries stringify identically to the
  // filtered ones, so useQuery's cache serves them without a second request.
  const unfilteredQuery = { ...query, model: undefined, agent_id: undefined };
  const modelOptionsQuery = useModelRollups(unfilteredQuery);
  const agentOptionsQuery = useAgentRollups(unfilteredQuery);

  // Rollups carry no descriptions — join them from the agents registry for
  // the roster's description column (best-effort; the list renders without).
  const registry = useAgents();
  // Published insights per agent, for the roster's recommendations pill. One
  // read for the whole workspace rather than one per row.
  const insightCounts = useInsightCounts();
  const descriptions = Object.fromEntries(
    (registry.data ?? []).map((a) => [a.agent_id, a.description]),
  );
  // An agent's `used` latch flips on its first request; the analytics rollups it
  // feeds are rebuilt every few minutes. So "used but nothing to show" is a
  // workspace waiting on its first rows — never an empty one (OPSS-323).
  const anyUsed = (registry.data ?? []).some((a) => a.used);

  // The latch never resets, so on its own it would claim every empty window is
  // priming, forever. Two bounds: it has to have flipped recently (it flips via
  // an update, so updated_at tracks it), and nothing may be narrowing the
  // window — zero calls under a filter is a real empty result, not a wait. The
  // roster reads the same map, so a row can never contradict the tiles.
  const primingSince = dashboardNow().getTime() - PRIMING_WINDOW_MS;
  const recentlyUsed = Object.fromEntries(
    (registry.data ?? []).map((a) => [
      a.agent_id,
      activeFilterCount === 0 && a.used && utcMs(a.updated_at) >= primingSince,
    ]),
  );
  const priming =
    Object.values(recentlyUsed).some(Boolean) &&
    summary.data?.data.call_count === 0;

  // Empty workspace (rollups loaded, zero agents): the design's overview
  // empty state (Figma 271:5434) ghosts the whole dashboard behind a scrim
  // and centers the connect CTA on a soft glow. The CTA opens /welcome.
  //
  // Both reads must have landed: `anyUsed` reads the registry, so gating on the
  // rollups alone would flash the connect CTA at a workspace that has an agent
  // (and strand it there for good if the registry read fails).
  //
  // `(untagged)` is a synthetic traffic bucket, not an agent (OPSS-352), so it
  // doesn't count as one here: a workspace whose only rollup row is that bucket
  // has nothing connected yet and still needs the connect CTA.
  const isEmpty =
    registry.data !== undefined &&
    !anyUsed &&
    rollups.data !== undefined &&
    rollups.data.data.items.every((a) => a.agent_id === UNTAGGED_AGENT);

  // One tick re-keys every mounted analytics hook, so the whole page picks the
  // rows up as soon as they exist; it stops the moment they do.
  useEffect(() => {
    if (!priming) return;

    const id = setInterval(bumpRefresh, PRIMING_POLL_MS);
    return () => clearInterval(id);
  }, [priming, bumpRefresh]);

  // The reads behind the overlay have nothing to return for an agent-less
  // workspace, so the ghosted dashboard came out as zeros and skeletons. Behind
  // the overlay it shows illustrative figures instead — display-only, and only
  // while the overlay covers them (see emptyStateDemo).
  const s = isEmpty ? DEMO_SUMMARY : summary.data?.data;
  const agentRows = isEmpty ? DEMO_AGENTS : (rollups.data?.data.items ?? []);
  const modelRows = isEmpty
    ? DEMO_MODELS
    : (modelRollups.data?.data.items ?? []);
  const points = isEmpty
    ? demoTrendPoints(range.from, range.to, granularity)
    : trends.data?.data.points;

  const windowSaved = s ? windowSavings(s) : 0;
  const savedRatio = s && s.cost.total > 0 ? windowSaved / s.cost.total : 0;
  const trendCaption =
    range.preset === "custom"
      ? "selected range"
      : `last ${
          { "1h": "hour", "1d": "24 hours", "7d": "7 days", "30d": "30 days" }[
            range.preset
          ] ?? range.preset
        }`;

  return (
    <div className="relative">
      {isEmpty && <EmptyOverlay onConnect={() => wsNav("/welcome")} />}
      <section
        aria-hidden={isEmpty || undefined}
        className={isEmpty ? "pointer-events-none select-none" : undefined}
      >
        {/* Page header — anchors the visual hierarchy (matters most when a
            banner sits above the fold). Same look as the page's other section
            titles ("Your connected agents", "Spend & savings"). */}
        {/* h2 for semantics; font-normal undoes the heading's default bold so
            it weighs the same as the sibling <p> titles. */}
        <div className="mb-3 flex items-start justify-between gap-4">
          <h2
            className="tw-float-up cursor-default text-[15px] font-normal leading-none"
            style={{ color: "var(--text)" }}
          >
            Your AI spend
            {priming && (
              <span
                className="ml-2.5 text-[13px]"
                style={{ color: "var(--text-muted)" }}
              >
                Preparing your dashboard. First usage appears within ~2 minutes.
              </span>
            )}
          </h2>
          {/* Model + agent filters (OPSS-421). Page-level rather than in the app
              header, where the timeframe lives: these narrow the dashboard's
              breakdowns and mean nothing on the settings/org pages that share
              that header. Hidden behind the empty-state overlay, which has no
              real data to filter. */}
          {!isEmpty && (
            <DashboardFilters
              modelOptions={(modelOptionsQuery.data?.data.items ?? []).map(
                (m) => m.model,
              )}
              selectedModels={models}
              onModelsChange={setModels}
              // `(untagged)` is a synthetic bucket for traffic the gateway saw
              // no x-agent-id on (OPSS-352), not an agent. It must not be an
              // option: the BFF stores that traffic under a different sentinel,
              // so filtering by it would match nothing and read as a bug.
              agentOptions={(agentOptionsQuery.data?.data.items ?? [])
                .filter((a) => a.agent_id !== UNTAGGED_AGENT)
                .map((a) => ({
                  value: a.agent_id,
                  label: a.name ?? a.agent_id,
                }))}
              selectedAgents={agentIds}
              onAgentsChange={setAgentIds}
              onClear={resetFilters}
            />
          )}
        </div>

        {/* KPIs */}
        {s && !priming ? (
          <KpiRow
            summary={s}
            onOpenFailures={() => {
              trackButtonPressed("Failed requests", "workspace");
              setFailuresOpen(true);
            }}
          />
        ) : (
          <KpiSkeleton />
        )}

        {/* Routing insight (placeholder — no recommendations API yet).
         * Hidden for now until a real recommendations API ships; keep the
         * component below so it's a one-line re-enable.
         * <RoutingInsightBanner onApply={() => navigate(ws("/settings"))} /> */}

        {/* Connected agents */}
        <div className="mt-5">
          <ConnectedAgentsCard
            agents={agentRows}
            descriptions={descriptions}
            recentlyUsed={recentlyUsed}
            recommendations={isEmpty ? undefined : insightCounts.data}
            loading={!isEmpty && rollups.initialLoading}
            onSelect={(agent) =>
              wsNav(`/agents/${encodeURIComponent(agent.agent_id)}`)
            }
          />
        </div>

        {/* Spend & savings — equal-width chart pair under a section header */}
        <div className="mt-5">
          <p
            className="cursor-default text-[15px] leading-none"
            style={{ color: "var(--text)" }}
          >
            Spend &amp; savings
          </p>
          <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* The framed card mounts only once the data is in — mounting it
                earlier (with the skeleton inside) would play the float-up
                entrance on the empty frame instead of the chart. */}
            {points && s && !priming ? (
              <div
                className="tw-float-up h-[336px] rounded-[5px] border border-[var(--border)] p-[17px]"
                style={{ "--float-delay": "0.3s" } as CSSProperties}
              >
                <TrendChart
                  points={points}
                  savedRatio={savedRatio}
                  caption={trendCaption}
                  from={range.from}
                  to={range.to}
                  granularity={granularity}
                />
              </div>
            ) : (
              <div className="h-[336px] animate-pulse rounded-[5px] bg-[var(--surface-2)]" />
            )}
            {s && !priming ? (
              <CostByTokenClass cost={s.cost} />
            ) : (
              <div className="h-[336px] animate-pulse rounded-[5px] bg-[var(--surface-2)]" />
            )}
          </div>
        </div>

        {/* Models used across all agents — the pooled per-model view of the same
            spend the roster splits per agent. Below the charts: the roster and
            the spend/savings pair answer "who is spending and on what", and this
            answers "against which models", which is the follow-up question. */}
        <div className="mt-5">
          <ModelsCard
            models={modelRows}
            loading={!isEmpty && modelRollups.initialLoading}
          />
        </div>
      </section>

      <FailuresPanel
        open={failuresOpen}
        onClose={() => setFailuresOpen(false)}
        onOpenAgent={(agentId) =>
          wsNav(`/agents/${encodeURIComponent(agentId)}`)
        }
      />
    </div>
  );
}

/* The design's overview empty state (Figma 271:5434): a page scrim ghosting
 * the dashboard, a soft radial glow, and the centered connect CTA. Insets are
 * negative to also cover the app shell's main padding.
 *
 * The scrim is a blur plus a lighter wash rather than the near-opaque one it
 * started as: the dashboard behind it is now a preview worth seeing through
 * (see emptyStateDemo), so it should read as out-of-focus, not as erased. */
function EmptyOverlay({ onConnect }: { onConnect: () => void }) {
  return (
    <div
      className="absolute z-10 flex flex-col items-center justify-center text-center"
      style={{
        inset: "-16px -20px",
        background: "color-mix(in srgb, var(--page) 62%, transparent)",
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
      }}
    >
      <div
        className="tw-float-up flex flex-col items-center px-[120px] py-[100px]"
        style={{
          background:
            "radial-gradient(closest-side, var(--empty-glow), transparent)",
        }}
      >
        <p className="text-[20px] font-medium" style={{ color: "var(--text)" }}>
          Connect an agent to see
          <br /> where it's wasting tokens
        </p>
        <p
          className="mt-3 max-w-[280px] text-[13px] leading-[1.3]"
          style={{ color: "var(--text-muted)" }}
        >
          Point your agent at the gateway with a simple one-line setup to start
          getting savings recs.
        </p>
        <button
          onClick={onConnect}
          className="mt-7 flex items-center gap-2.5 rounded-[2px] px-[15px] py-[9px] text-[13px] transition-opacity hover:opacity-90"
          style={{ background: "var(--text)", color: "var(--page)" }}
        >
          <Plus size={13} /> Connect your first agent
        </button>
      </div>
    </div>
  );
}

function KpiRow({
  summary,
  onOpenFailures,
}: {
  summary: Summary;
  onOpenFailures: () => void;
}) {
  const saved = windowSavings(summary);
  const net = summary.cost.total;
  const pctSaved = savedPct(saved, net);
  const { input, output, cache_read, cache_write } = summary.tokens;
  const cacheTokens = cache_read + cache_write;
  const totalTokens = input + output + cacheTokens;
  // A sub-slice of the prompt tokens above (not added to the total), OPSS-289.
  const imageTokens = summary.tokens.image_tokens ?? 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {/* Savings — the mint feature card with the tick-gauge strip */}
      <CostReductionCard
        pct={pctSaved}
        caption={`${formatUSD(saved)} saved`}
        hint={SAVINGS_HINT}
      />

      <StatCard
        label="Spend"
        Icon={DollarSign}
        value={formatUSD(net)}
        floatDelay={0.06}
        hint={SPEND_HINT}
      >
        {/* Spend counts only requests that reached a provider, but call_count counts
            every attempt — so a window with failures has to say so, or it reads as a
            lot of work for very little money (OPSS-256). The count is the way into
            the failure detail behind it (OPSS-352). */}
        <SubStat
          val={formatCompact(summary.call_count)}
          unit="requests"
          note={
            summary.failed_calls > 0
              ? `${formatCompact(summary.failed_calls)} failed`
              : undefined
          }
          noteLabel="View failed requests"
          onNoteClick={onOpenFailures}
        />
      </StatCard>

      <StatCard
        label="Sessions"
        Icon={Activity}
        value={formatCompact(summary.session_count)}
        floatDelay={0.12}
      >
        <SubStat val={String(summary.models.length)} unit="models" />
      </StatCard>

      <StatCard
        label="Tokens"
        Icon={Layers}
        value={formatCompact(totalTokens)}
        floatDelay={0.18}
      >
        <TokenBreakdown
          rows={[
            ["Input", formatCompact(input)],
            ["Output", formatCompact(output)],
            ["Cache", formatCompact(cacheTokens)],
            ...(imageTokens > 0
              ? ([["Images", formatCompact(imageTokens)]] as [string, string][])
              : []),
          ]}
        />
      </StatCard>
    </div>
  );
}

/* Static routing recommendation. The v1 analytics contract exposes no
 * recommendations endpoint yet, so this is a placeholder insight (dismissible)
 * that points at the config surface. Replace when a recommendations API ships.
 *
 * Temporarily hidden (see OverviewBody). Kept commented rather than deleted so
 * it's a quick re-enable once a real recommendations API lands. When restoring,
 * also re-add the `useState`, `ArrowRight`, and `X` imports it needs.
 *
function RoutingInsightBanner({ onApply }: { onApply: () => void }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <div
      className="relative mb-3 flex items-center gap-7 overflow-hidden py-6 pl-5 pr-8"
      style={{
        background: "color-mix(in srgb, var(--mark-amber) 7%, var(--surface))",
        border: "1px solid rgba(232,151,58,0.5)",
        minHeight: "104px",
      }}
    >
      <div
        className="flex-shrink-0 pr-7"
        style={{ borderRight: "1px solid rgba(178,106,46,0.28)" }}
      >
        <div
          className="tw-display text-[42px] font-light leading-none tabular-nums"
          style={{ color: "#B26A2E" }}
        >
          22%
        </div>
        <div
          className="mt-1.5 text-[11px] uppercase tracking-[0.1em]"
          style={{ color: "var(--text-muted)" }}
        >
          more savings
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-2.5 flex items-center gap-3">
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.14em]"
            style={{ background: "rgba(232,151,58,0.22)", color: "#B26A2E" }}
          >
            Routing insight
          </span>
          <p
            className="tw-display text-[13px]"
            style={{ color: "var(--text)" }}
          >
            Route eligible calls through a cheaper model to cut spend without
            hurting quality
          </p>
        </div>
        <div
          className="mb-1.5 flex justify-between text-[11px]"
          style={{ color: "var(--text-muted)" }}
        >
          <span>
            Current{" "}
            <span className="font-mono text-[var(--text)]">baseline</span>
          </span>
          <span>
            Optimized{" "}
            <span className="font-mono" style={{ color: "#B26A2E" }}>
              −22%
            </span>
          </span>
        </div>
        <div
          className="flex h-2.5"
          style={{ background: "rgba(178,106,46,0.14)" }}
        >
          <div style={{ width: "78%", background: "#D19A4F" }} />
          <div
            style={{
              width: "22%",
              background:
                "repeating-linear-gradient(90deg,#B26A2E,#B26A2E 1px,transparent 1px,transparent 5px)",
            }}
          />
        </div>
      </div>
      <button
        onClick={onApply}
        className="flex flex-shrink-0 items-center gap-2 self-center px-5 py-1.5 text-[12px] font-semibold transition-opacity hover:opacity-90"
        style={{ background: "var(--text)", color: "var(--surface)" }}
      >
        Apply <ArrowRight size={12} />
      </button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss insight"
        className="absolute right-3 top-3 transition-colors"
        style={{ color: "rgba(60,20,30,0.3)" }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
*/
